import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import RichEditor from "../components/RichEditor";
import { toast } from "sonner";
import { Megaphone, Plus, PaperPlaneRight, Eye, TrashSimple, Users as UsersIcon, ChartBar, ArrowClockwise, Copy } from "@phosphor-icons/react";

const SEGMENT_OPTIONS = [
  { value: "technicians",           label: "Techniciens (admins + techs)" },
  { value: "clients_maintenance",   label: "Clients avec contrat de maintenance actif" },
  { value: "all_clients",           label: "Tous les clients" },
  { value: "tag",                   label: "Segment personnalisé par tag" },
];

const VARIABLE_HELP = [
  ["{prenom}",                      "Prénom du destinataire"],
  ["{nom}",                         "Nom du destinataire"],
  ["{nom_complet}",                 "Nom complet"],
  ["{societe}",                     "Nom de la société"],
  ["{contrat_heures_restantes}",    "Heures restantes sur le contrat annuel"],
  ["{campagne}",                    "Nom de la campagne"],
  ["{desabo_url}",                  "Lien de désinscription (obligatoire RGPD)"],
];

const STATUS_LABEL = {
  draft: "Brouillon",
  scheduled: "Programmée",
  sending: "En cours d'envoi",
  sent: "Envoyée",
  failed: "Échec",
};

const STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  sending: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  sent: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

// Format an ISO datetime as "15/03/2026 09:30" in Europe/Paris.
const fmtScheduled = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
// Convert an ISO datetime to the "YYYY-MM-DDTHH:MM" format expected by <input type="datetime-local">.
const toLocalDT = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function Newsletter() {
  return (
    <div className="space-y-6 max-w-7xl">
      <div>
        <div className="label-caps">Marketing</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-3">
          <Megaphone size={28} weight="duotone" className="text-primary"/> Newsletter
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Envoyez des campagnes ciblées à vos techniciens, clients ou segments personnalisés
          via la boîte partagée Microsoft 365. Rate-limit configurable, tracking d'ouverture &amp;
          de clics, désinscription en un clic (RGPD).
        </p>
      </div>

      <Tabs defaultValue="campaigns" className="w-full">
        <TabsList className="mb-4" data-testid="newsletter-tabs">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns"><PaperPlaneRight size={14} className="mr-1.5"/> Campagnes</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates"><Copy size={14} className="mr-1.5"/> Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns"><CampaignsPanel/></TabsContent>
        <TabsContent value="templates"><TemplatesPanel/></TabsContent>
      </Tabs>
    </div>
  );
}


/* ============================== TEMPLATES ================================= */
function TemplatesPanel() {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null); // null | {id?, name, subject, body_html}
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/newsletter/templates"); setItems(r.data || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      name: editor.name.trim(),
      subject: editor.subject.trim(),
      body_html: editor.body_html || "",
      from_name: editor.from_name?.trim() || null,
    };
    if (!payload.name || !payload.subject) return toast.error("Nom et sujet requis");
    try {
      if (editor.id) await api.put(`/newsletter/templates/${editor.id}`, payload);
      else await api.post("/newsletter/templates", payload);
      toast.success("Template enregistré");
      setEditor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le template « ${t.name} » ?`)) return;
    try { await api.delete(`/newsletter/templates/${t.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="new-template-btn" onClick={() => setEditor({ name: "", subject: "", body_html: "", from_name: "" })}>
          <Plus size={16} className="mr-1.5"/> Nouveau template
        </Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> :
       items.length === 0 ? (
         <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
           Aucun template. Cliquez sur « Nouveau template » pour commencer.
         </CardContent></Card>
       ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Card key={t.id} data-testid={`template-card-${t.id}`}>
              <CardHeader className="pb-2"><CardTitle className="text-base line-clamp-1">{t.name}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="text-xs text-muted-foreground line-clamp-2">{t.subject}</div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => setEditor(t)}>Modifier</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-red-600 hover:text-red-700">
                    <TrashSimple size={14}/>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TemplateEditor editor={editor} onClose={() => setEditor(null)} onSave={save} onChange={setEditor}/>
    </div>
  );
}

function TemplateEditor({ editor, onClose, onSave, onChange }) {
  if (!editor) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editor.id ? "Modifier le template" : "Nouveau template"}</DialogTitle>
          <DialogDescription>Utilisez des variables comme {"{prenom}"} ou {"{societe}"} — elles seront remplacées à l'envoi.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom interne *</Label>
            <Input data-testid="tpl-name" value={editor.name} onChange={(e) => onChange({...editor, name: e.target.value})}
                   placeholder="Ex. Newsletter mensuelle mars"/></div>
          <div><Label>Sujet de l'email *</Label>
            <Input data-testid="tpl-subject" value={editor.subject} onChange={(e) => onChange({...editor, subject: e.target.value})}
                   placeholder="Ex. {prenom}, votre bilan {campagne}"/></div>
          <div><Label>Corps du message *</Label>
            <RichEditor value={editor.body_html || ""} onChange={(v) => onChange({...editor, body_html: v})} minHeight={280}/></div>
          <VariableList/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onSave} data-testid="tpl-save-btn">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================== CAMPAIGNS ================================= */
function CampaignsPanel() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [editor, setEditor] = useState(null);
  const [stats, setStats] = useState(null); // {id, data}
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, t, tg] = await Promise.all([
        api.get("/newsletter/campaigns"),
        api.get("/newsletter/templates"),
        api.get("/newsletter/tags"),
      ]);
      setItems(c.data || []); setTemplates(t.data || []); setTags(tg.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Poll every 4s when a campaign is sending.
  useEffect(() => {
    const active = items.some((c) => c.status === "sending");
    if (!active) return;
    const int = setInterval(load, 4000);
    return () => clearInterval(int);
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="new-campaign-btn" onClick={() => setEditor({
          name: "", subject: "", body_html: "", from_name: "",
          segment_type: "all_clients", segment_value: "",
          rate_per_minute: 30, template_id: "", scheduled_at: "",
        })}>
          <Plus size={16} className="mr-1.5"/> Nouvelle campagne
        </Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> :
       items.length === 0 ? (
         <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="campaigns-empty">
           Aucune campagne. Créez votre première pour envoyer une newsletter à vos contacts.
         </CardContent></Card>
       ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <CampaignRow key={c.id} camp={c} onEdit={() => setEditor(c)} onStats={() => setStats({id: c.id})} onRefresh={load}/>
          ))}
        </div>
      )}

      <CampaignEditor editor={editor} templates={templates} tags={tags}
        onClose={() => setEditor(null)}
        onSaved={() => { setEditor(null); load(); }}/>
      {stats && <CampaignStatsDialog id={stats.id} onClose={() => setStats(null)}/>}
    </div>
  );
}

function CampaignRow({ camp, onEdit, onStats, onRefresh }) {
  const remove = async () => {
    if (!window.confirm(`Supprimer la campagne « ${camp.name} » ?`)) return;
    try { await api.delete(`/newsletter/campaigns/${camp.id}`); toast.success("Supprimée"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const launch = async () => {
    if (!window.confirm(`Lancer l'envoi de la campagne « ${camp.name} » ?\nUne fois lancé l'envoi ne peut être arrêté.`)) return;
    try {
      const r = await api.post(`/newsletter/campaigns/${camp.id}/send`);
      toast.success(`Envoi lancé — ${r.data.total} destinataire(s) @ ${r.data.rate_per_minute}/min`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const schedule = async () => {
    if (!camp.scheduled_at) return toast.error("Renseignez d'abord une date de planification dans l'éditeur.");
    try {
      await api.post(`/newsletter/campaigns/${camp.id}/schedule`);
      toast.success("Campagne programmée");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const cancelSchedule = async () => {
    try {
      await api.post(`/newsletter/campaigns/${camp.id}/cancel-schedule`);
      toast.success("Planification annulée");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const total = camp.total || 0;
  const sent = camp.sent_count || 0;
  const pct = total ? Math.round(100 * sent / total) : 0;

  return (
    <Card data-testid={`campaign-row-${camp.id}`}>
      <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{camp.name}</span>
            <span className={`text-[0.7rem] px-2 py-0.5 rounded ${STATUS_CLASS[camp.status] || STATUS_CLASS.draft}`}>
              {STATUS_LABEL[camp.status] || "Brouillon"}
            </span>
            {camp.status === "scheduled" && camp.scheduled_at && (
              <span className="text-xs text-blue-700 dark:text-blue-300" data-testid={`campaign-scheduled-${camp.id}`}>
                ⏰ Envoi prévu le {fmtScheduled(camp.scheduled_at)} — {total} destinataire(s)
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{camp.subject}</div>
          {camp.status === "sending" && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden">
                <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }}/>
              </div>
              <span className="text-xs text-muted-foreground shrink-0">{sent}/{total}</span>
            </div>
          )}
          {camp.status === "sent" && (
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>📤 {camp.sent_count} envoyés</span>
              <span>👁 {camp.opened_count || 0} ouverts</span>
              <span>👆 {camp.clicked_count || 0} clics</span>
              <span>🚫 {camp.unsubscribed_count || 0} désabo</span>
            </div>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="ghost" onClick={onStats} data-testid={`campaign-stats-${camp.id}`}><ChartBar size={14}/></Button>
          {camp.status === "scheduled" && (
            <Button size="sm" variant="outline" onClick={cancelSchedule} data-testid={`campaign-cancel-${camp.id}`}>
              Annuler
            </Button>
          )}
          {(!camp.status || camp.status === "draft" || camp.status === "failed") && (
            <>
              <Button size="sm" variant="outline" onClick={onEdit}>Modifier</Button>
              {camp.scheduled_at && (
                <Button size="sm" variant="secondary" onClick={schedule} data-testid={`campaign-schedule-${camp.id}`}>
                  ⏰ Programmer
                </Button>
              )}
              <Button size="sm" onClick={launch} data-testid={`campaign-send-${camp.id}`}>
                <PaperPlaneRight size={14} className="mr-1"/> Envoyer
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={remove}><TrashSimple size={14}/></Button>
            </>
          )}
          {camp.status === "sent" && <Button size="sm" variant="outline" onClick={onStats}>Détails</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

function CampaignEditor({ editor, templates, tags, onClose, onSaved }) {
  const [seg, setSeg] = useState({ count: 0, sample: [] });
  useEffect(() => {
    if (!editor) return;
    (async () => {
      try {
        const r = await api.get("/newsletter/segments/preview", {
          params: { type: editor.segment_type, value: editor.segment_value || "" },
        });
        setSeg(r.data);
      } catch { setSeg({ count: 0, sample: [] }); }
    })();
  }, [editor?.segment_type, editor?.segment_value]);

  if (!editor) return null;

  const update = (patch) => Object.assign(editor, patch); // caller sets via onChange in child
  const setField = (patch) => { onSaved.__stub__; }; // placeholder — never used

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return editor;
    return { ...editor, template_id: tid, subject: t.subject, body_html: t.body_html, from_name: t.from_name || editor.from_name };
  };

  const save = async () => {
    const payload = {
      name: editor.name.trim(),
      subject: editor.subject.trim(),
      body_html: editor.body_html || "",
      from_name: editor.from_name?.trim() || null,
      segment_type: editor.segment_type,
      segment_value: editor.segment_type === "tag" ? (editor.segment_value?.trim() || null) : null,
      rate_per_minute: Math.max(1, Math.min(600, parseInt(editor.rate_per_minute, 10) || 30)),
      template_id: editor.template_id || null,
      scheduled_at: editor.scheduled_at ? new Date(editor.scheduled_at).toISOString() : null,
    };
    if (!payload.name || !payload.subject) return toast.error("Nom et sujet requis");
    if (payload.segment_type === "tag" && !payload.segment_value) return toast.error("Choisissez un tag");
    try {
      if (editor.id) await api.put(`/newsletter/campaigns/${editor.id}`, payload);
      else await api.post("/newsletter/campaigns", payload);
      toast.success("Campagne enregistrée");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="campaign-editor">
        <DialogHeader>
          <DialogTitle>{editor.id ? "Modifier la campagne" : "Nouvelle campagne"}</DialogTitle>
          <DialogDescription>Choisissez un segment, préparez le message et enregistrez avant de lancer l'envoi.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div><Label>Nom interne *</Label>
              <Input data-testid="camp-name" value={editor.name} onChange={(e) => { editor.name = e.target.value; onSaved.__noop__; setSeg({...seg}); }}/></div>
            <div><Label>Sujet de l'email *</Label>
              <Input data-testid="camp-subject" value={editor.subject}
                     onChange={(e) => { editor.subject = e.target.value; setSeg({...seg}); }}/></div>

            <div><Label>Segment de destinataires *</Label>
              <Select value={editor.segment_type}
                      onValueChange={(v) => { editor.segment_type = v; editor.segment_value = ""; setSeg({count:0,sample:[]}); }}>
                <SelectTrigger data-testid="camp-segment-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {SEGMENT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editor.segment_type === "tag" && (
              <div><Label>Tag utilisateur</Label>
                <Input list="segment-tags" value={editor.segment_value || ""}
                       onChange={(e) => { editor.segment_value = e.target.value; setSeg({...seg}); }}
                       placeholder="Ex. vip"
                       data-testid="camp-segment-value"/>
                <datalist id="segment-tags">{tags.map((t) => <option key={t} value={t}/>)}</datalist>
              </div>
            )}

            <div className="p-3 rounded bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
                 data-testid="camp-segment-preview">
              <div className="flex items-center gap-2 text-sm font-medium">
                <UsersIcon size={16}/> {seg.count} destinataire(s)
              </div>
              {seg.sample.length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                  Aperçu : {seg.sample.slice(0, 5).map((s) => s.name).join(", ")}{seg.count > 5 ? "…" : ""}
                </div>
              )}
            </div>

            <div><Label>Rythme d'envoi (mails / minute)</Label>
              <Input type="number" min="1" max="600" value={editor.rate_per_minute}
                     onChange={(e) => { editor.rate_per_minute = e.target.value; setSeg({...seg}); }}
                     data-testid="camp-rate"/>
              <p className="text-xs text-muted-foreground mt-1">
                Microsoft Graph accepte ~30/min sur une boîte partagée. Baissez si vous avez un tenant strict.
              </p>
            </div>

            <div>
              <Label>Planification (facultatif)</Label>
              <Input type="datetime-local"
                     value={toLocalDT(editor.scheduled_at)}
                     onChange={(e) => { editor.scheduled_at = e.target.value; setSeg({...seg}); }}
                     data-testid="camp-scheduled-at"/>
              <p className="text-xs text-muted-foreground mt-1">
                Laissez vide pour un envoi immédiat depuis la liste. Renseigné, un bouton
                « ⏰ Programmer » apparaît sur la ligne pour lancer l'envoi automatique à cette date.
              </p>
            </div>

            {templates.length > 0 && (
              <div><Label>Charger depuis un template</Label>
                <Select value={editor.template_id || ""} onValueChange={(v) => { const next = applyTemplate(v); Object.assign(editor, next); setSeg({...seg}); }}>
                  <SelectTrigger data-testid="camp-template"><SelectValue placeholder="Aucun (rédaction libre)"/></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div><Label>Corps du message *</Label>
              <RichEditor value={editor.body_html || ""}
                          onChange={(v) => { editor.body_html = v; setSeg({...seg}); }}
                          minHeight={340}/></div>
            <VariableList compact/>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          {editor.id && (
            <>
              <Button variant="secondary" onClick={async () => {
                try {
                  const r = await api.post(`/newsletter/campaigns/${editor.id}/preview`);
                  const w = window.open("", "_blank", "width=720,height=800");
                  if (w) { w.document.write(r.data.html); w.document.title = r.data.subject; }
                } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
              }} data-testid="camp-preview-btn"><Eye size={14} className="mr-1"/> Aperçu</Button>
              <Button variant="secondary" onClick={async () => {
                const to = window.prompt("Envoyer un test à quelle adresse ?", editor._lastTestTo || "");
                if (!to) return;
                editor._lastTestTo = to;
                try {
                  const r = await api.post(`/newsletter/campaigns/${editor.id}/test`, { to });
                  toast.success(`Test envoyé à ${r.data.sent_to}`);
                } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
              }} data-testid="camp-test-btn">
                <PaperPlaneRight size={14} className="mr-1"/> Envoyer un test
              </Button>
            </>
          )}
          <Button onClick={save} data-testid="camp-save-btn">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignStatsDialog({ id, onClose }) {
  const [data, setData] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const load = async () => {
    try {
      const [s, r] = await Promise.all([
        api.get(`/newsletter/campaigns/${id}/stats`),
        api.get(`/newsletter/campaigns/${id}/recipients`),
      ]);
      setData(s.data); setRecipients(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(); }, [id]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="campaign-stats-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ChartBar size={18}/> Statistiques</DialogTitle>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Envoyés" value={data.sent} total={data.total}/>
              <Stat label="Ouverts" value={data.opened} pct={data.open_rate_pct} tone="emerald"/>
              <Stat label="Clics" value={data.clicked} pct={data.click_rate_pct} tone="blue"/>
              <Stat label="Désabo" value={data.unsubscribed} pct={data.unsubscribe_rate_pct} tone="rose"/>
            </div>
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={load}><ArrowClockwise size={14} className="mr-1"/> Rafraîchir</Button>
            </div>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-muted-foreground">
                  <tr><th className="text-left p-2">Email</th><th className="p-2">Envoyé</th><th className="p-2">Ouvert</th><th className="p-2">Cliqué</th><th className="p-2">Désabo</th></tr>
                </thead>
                <tbody>
                  {recipients.slice(0, 200).map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="p-2 truncate max-w-[220px]">{r.email}</td>
                      <td className="p-2 text-center text-xs">{r.sent_at ? "✓" : ""}</td>
                      <td className="p-2 text-center text-xs">{r.opened_at ? "👁" : ""}</td>
                      <td className="p-2 text-center text-xs">{r.clicked_at ? "👆" : ""}</td>
                      <td className="p-2 text-center text-xs">{r.unsubscribed_at ? "🚫" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {recipients.length > 200 && <div className="p-2 text-xs text-muted-foreground text-center">
                Affichage limité aux 200 premiers destinataires ({recipients.length} au total).
              </div>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, total, pct, tone = "slate" }) {
  const toneCls = {
    emerald: "text-emerald-700 dark:text-emerald-300",
    blue: "text-blue-700 dark:text-blue-300",
    rose: "text-rose-700 dark:text-rose-300",
    slate: "text-slate-700 dark:text-slate-300",
  }[tone];
  return (
    <div className="p-3 border rounded bg-white dark:bg-slate-950">
      <div className="text-xs uppercase text-muted-foreground tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneCls}`}>{value ?? 0}</div>
      <div className="text-xs text-muted-foreground">
        {total ? `sur ${total}` : `${pct ?? 0}%`}
      </div>
    </div>
  );
}

function VariableList({ compact = false }) {
  return (
    <div className={`p-3 rounded bg-slate-50 dark:bg-slate-900 border text-xs ${compact ? "" : "mt-2"}`}>
      <div className="font-medium mb-1.5">Variables disponibles</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-3">
        {VARIABLE_HELP.map(([v, help]) => (
          <div key={v} className="flex items-baseline gap-2">
            <code className="text-primary shrink-0">{v}</code>
            <span className="text-muted-foreground truncate">{help}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
